import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from '@core/js/utils/classNames.util';
import { pick, pipe, path } from 'ramda';
import { useLocation } from 'react-router-dom';
import { dispatcher } from 'dispatcher';
import { Link } from '@core/js/components/common/Link';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { useOpenChat, useOpenInbox } from '@core/js/components/system/CommunicationWidgets/hooks';

import * as hooks from '@core/js/components/main/NavigationBar/hooks';
import * as helpers from './helpers';

import style from './NavigationBar.scss';

export const NavigationBar = ({ currentPageURL }) => {
    const location = useLocation();
    const contactPreferences = useUserSelector(path(['userDetails', 'contactPreferences'])); /* TotalCasino changes */
    const { rootPage, menuItems, chatWidget, inboxWidget } = useAppSelector(pick(['rootPage', 'menuItems', 'chatWidget', 'inboxWidget']));
    const isActive = item => item.url === currentPageURL || currentPageURL === '/' && item.url === rootPage;
    const filterByRoleAvailability = hooks.useFilterByRoleAvailability();
    const [showInbox, setShowInbox] = useState(helpers.getShowInboxIcon(contactPreferences));

    /* TotalCasino changes */
    useEffect(() => {
        const setShowInboxEvent = (data) => {
            const existingKeys = new Set(data.map(p => `${p.channel}:${p.type}`));
            const preferences = new Set([
                ...data,
                ...(contactPreferences || []).filter(pref => !existingKeys.has(`${pref.channel}:${pref.type}`))
            ]);
            setShowInbox(
                helpers.getShowInboxIcon([...preferences])
            );
        };

        dispatcher.listen('user:details:update', setShowInboxEvent);
        dispatcher.listen('user:toggleInboxInNavigation', setShowInboxEvent);

        return () => {
            dispatcher.removeListener('user:details:update', setShowInboxEvent);
            dispatcher.removeListener('user:toggleInboxInNavigation', setShowInboxEvent);
        };
    }, [contactPreferences]);
    /* TotalCasino changes */

    const onOpenInbox = useOpenInbox();
    const onOpenChat = useOpenChat();

    const currentMenuItems = useMemo(() => menuItems?.length ? pipe(
        helpers.getMenuSiblingsByUrl(currentPageURL || rootPage),
        filterByRoleAvailability,
        helpers.markMenuItemAsOpener(chatWidget, inboxWidget),
        helpers.filterByUrlAvailability(location.search)
    )(menuItems) : [], [
        location,
        rootPage,
        menuItems,
        chatWidget,
        inboxWidget,
        currentPageURL,
        filterByRoleAvailability
    ]);

    if (!currentMenuItems.length) {
        return null;
    }

    return (
        <div className={`${style.container} cms-navigation-bar`}>
            <ul className={style.list}>
                {currentMenuItems.map(item => (
                    <div key={item.friendlyURL}>
                        {item.inboxOpener && showInbox && (/* TotalCasino changes */
                            <li className={clsx([style.listItem, item.cssClasses])}>
                                <a
                                   href='#'
                                   className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                   onClick={onOpenInbox}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'UNREAD_MESSAGES_COUNT'} viewName={'unreadmessagescount'} />
                                    </div>
                                </a>
                            </li>
                        )}
                        {item.chatOpener && (
                            <li className={clsx([style.listItem, item.cssClasses])}>
                                <a
                                   href='#'
                                   className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                   onClick={onOpenChat}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'CHAT_UNREAD'} viewName={'chatunread'} />
                                    </div>
                                </a>
                            </li>
                        )}
                        {!item.inboxOpener && !item.chatOpener && (
                            <li
                                className={clsx([style.listItem, item.cssClasses, isActive(item) && `${style.isActive} active`])}
                                data-url={item.url}
                                {...(item.dyUnit ? { 'data-dyUnit': `${item.dyUnit}` } : {})} /* TotalCasino changes */
                            >
                                <Link
                                    href={item.url}
                                    inNewWindow={item.target === '_blank'}
                                    className={`${style.link} ${style.listItemPoint}`}
                                >
                                    <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                    <span className={style.itemName}>{item.name}</span>
                                </Link>
                            </li>
                        )}
                    </div>
                ))}
            </ul>
        </div>
    );
};
